import "./assets/scss/PaymentMethodManager.scss";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import ConfirmationModal from "@components/ConfirmationModal";
import {
  AddEditButtonsContainer,
  AddPaymentMethodButtonWrapper,
  PaymentBody,
  PaymentContainer,
  PaymentOption,
  QuickPayRow,
  QuickPaySeparator,
} from "@components/PaymentMethod";
import { GatewayCard } from "@components/PaymentMethod/GatewayCard";
import { usePaymentCompletion } from "@hooks/billing/usePaymentCompletion";
import useDataLayer from "@hooks/useDataLayer";
import AmexSvg from "@img/card/Amex.svg";
import Discover from "@img/card/Discover.svg";
import MasterCardSvg from "@img/card/MasterCard.svg";
import VisaSvg from "@img/card/Visa.svg";
import LinkLogo from "@img/Link.svg";
import Loader from "@img/Loader.svg";
import WithFlexLogo from "@img/with-flex-logo.webp";
import { Button, Card, Text, Title } from "@runwayhealth/runway-components-react";
import {
  ADD_PAYMENT_METHOD,
  DELETE_PAYMENT_METHODS,
  GET_PAYMENT_METHODS,
  SET_DEFAULT_PAYMENT_METHOD,
  UPDATE_PAYMENT_METHOD,
} from "@store/user/userActions";
import { ExpressCheckoutElement, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import {
  PaymentRequest,
  Stripe,
  StripeElements,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from "@stripe/stripe-js";
import { GA4Events } from "@types-runway/analytics";
import {
  CardBrands,
  CardLogoIndex,
  PaymentGateway,
  PaymentManagerSetup,
  PaymentMethod,
  PaymentMethodManagerView,
  PaymentMethodType,
} from "@types-runway/billing";
import { RootEntity } from "@types-runway/state";
import { User } from "@types-runway/user";

import PaymentForm from "./components/PaymentForm/PaymentForm";
import { collectLinkPaymentMethodIds, dedupeLinkPaymentMethods } from "./linkPaymentMethods";

interface PaymentMethodManagerProps {
  stripeHook: Stripe | null;
  stripeElementsHook: StripeElements | null;
  user: RootEntity<User>;
  view: PaymentMethodManagerView;
  cardHidden?: boolean;
  promoCodeId?: string;
  caseId?: number;
  onGatewayChange?: (gateway: PaymentGateway) => void;
  paymentRequest?: PaymentRequest | null;
  canUseApplePay?: boolean;
  onApplePayButtonClick?: () => boolean;
  canUseLink?: boolean;
  onLinkReady?: (event: StripeExpressCheckoutElementReadyEvent) => void;
  onLinkConfirm?: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  isProcessingPayment?: boolean;
}

const PaymentMethodManager = ({
  stripeHook,
  stripeElementsHook,
  user,
  view,
  cardHidden,
  caseId,
  onGatewayChange,
  paymentRequest,
  canUseApplePay,
  canUseLink,
  onLinkReady,
  onLinkConfirm,
  onApplePayButtonClick,
  isProcessingPayment,
}: PaymentMethodManagerProps) => {
  const dispatch = useDispatch();
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway | null>(null);

  const paymentMethods = user.data.paymentMethods ?? [];
  const displayedPaymentMethods = dedupeLinkPaymentMethods(
    paymentMethods,
    user.data.defaultPaymentMethod
  );
  const [managerSetup, setManagerSetup] = useState<PaymentManagerSetup>(PaymentManagerSetup.ADD);
  const [removalPaymentMethodIds, setRemovalPaymentMethodIds] = useState<string[]>([]);

  // We use this to keep track of previous payment methods state.
  // Our interests on this are to keep track in analytics.
  const prevPaymentMethodsRef = useRef<PaymentMethod[] | undefined>(undefined);
  const { reportPurchasedProducts } = useDataLayer(GA4Events.ADD_PAYMENT_INFO);
  const paymentMethodsChangesRef = useRef<number>(0);

  useMemo(() => {
    dispatch({ type: GET_PAYMENT_METHODS });
  }, []);

  usePaymentCompletion({ caseId: caseId ?? 0 });

  useEffect(() => {
    // Count changes in payment methods.
    paymentMethodsChangesRef.current += 1;
    if (paymentMethods.length > 0) setManagerSetup(PaymentManagerSetup.SET_DEFAULT);

    // Only report add_payment_method_info if user is on a checkout session.
    // Also start reporting after payment methods initialization.
    // Which initialization happens after fetch to api during component mount.
    // So, we start reporting after the first payment method change.
    // First payment method change => Initialization of data.
    if (view === PaymentMethodManagerView.CHECKOUT && paymentMethodsChangesRef.current > 1) {
      if (prevPaymentMethodsRef.current) {
        const addedMethods = paymentMethods.filter(
          (method) => !prevPaymentMethodsRef.current?.some((pm) => pm.id === method.id)
        );

        if (addedMethods.length > 0) {
          reportPurchasedProducts();
        }
      }

      // Update reference with current payment methods after each render.
      prevPaymentMethodsRef.current = paymentMethods;
    }
  }, [paymentMethods]);

  const choosePaymentMethod = (e: React.MouseEvent<HTMLInputElement>) => {
    if (e.currentTarget.value) {
      setManagerSetup(PaymentManagerSetup.SET_DEFAULT);
      save(undefined, e.currentTarget.value);
    }
  };

  const addMethod = () => {
    setManagerSetup(PaymentManagerSetup.ADD);
  };
  const editMethod = () => {
    // if (!chosenPaymentMethod) {
    //   setChosenPaymentMethod(user.data.defaultPaymentMethod);
    // }
    setManagerSetup(PaymentManagerSetup.EDIT);
  };
  const deleteMethod = (methodId: string) => {
    const paymentMethod = paymentMethods.find((method) => method.id === methodId);
    const methodIds =
      paymentMethod?.type === PaymentMethodType.LINK
        ? collectLinkPaymentMethodIds(paymentMethods)
        : [methodId];

    setDisplayConfirmationModal(true);
    setRemovalPaymentMethodIds(methodIds);
  };

  const closePaymentForm = () => {
    setManagerSetup(PaymentManagerSetup.SET_DEFAULT);
  };
  const confirmDeleteMethod = (confirmation: boolean) => {
    if (confirmation) {
      dispatch({ type: DELETE_PAYMENT_METHODS, methodIds: removalPaymentMethodIds });
      if (paymentMethods.length === removalPaymentMethodIds.length) {
        setManagerSetup(PaymentManagerSetup.ADD);
      }
    }
    setDisplayConfirmationModal(false);
    setRemovalPaymentMethodIds([]);
  };

  const renderDeleteConfirmationMessage = (): string => {
    // For a collapsed Link entry, removalPaymentMethodIds holds every
    // underlying id — any of them identifies it as Link, so [0] is enough.
    const paymentMethod = paymentMethods.find(
      (paymentMethod) => paymentMethod.id === removalPaymentMethodIds[0]
    );

    if (paymentMethod?.type === PaymentMethodType.LINK) {
      return "Your Link payment method will be deleted. You can add it back at any time.";
    }

    const card = paymentMethod?.card;
    return `${card?.brand} ${card?.funding} card ending in ${card?.last4} will be deleted. You can add it back at any time, as long as it is still an active credit card.`;
  };

  const save = (paymentMethod?: PaymentMethod, chosenPaymentMethod?: string) => {
    if (paymentMethod) {
      if (managerSetup === PaymentManagerSetup.ADD) {
        dispatch({
          type: ADD_PAYMENT_METHOD,
          paymentMethodId: paymentMethod?.id,
          view,
        });
      } else if (managerSetup === PaymentManagerSetup.EDIT) {
        dispatch({
          type: UPDATE_PAYMENT_METHOD,
          paymentMethodUpdate: paymentMethod,
        });
      }
    } else {
      dispatch({
        type: SET_DEFAULT_PAYMENT_METHOD,
        methodId: chosenPaymentMethod,
        view,
      });
    }
  };

  const handleGatewaySelect = (gateway: PaymentGateway) => {
    setSelectedGateway(gateway);
    onGatewayChange?.(gateway);
  };

  const renderCreditCardContent = () => (
    <>
      {managerSetup === PaymentManagerSetup.SET_DEFAULT && (
        <>
          {displayedPaymentMethods.length > 0 && (
            <Title className="manager-title" as="h2" size="md-bold">
              Choose your preferred payment method
            </Title>
          )}

          {displayedPaymentMethods.map((paymentMethod) => {
            const isLinkPaymentMethod = paymentMethod.type === PaymentMethodType.LINK;

            return (
              <PaymentContainer key={paymentMethod.id}>
                <PaymentOption
                  id={paymentMethod.id}
                  checked={user.data.defaultPaymentMethod === paymentMethod.id}
                  value={paymentMethod.id}
                  onClick={choosePaymentMethod}
                >
                  <PaymentBody>
                    <div className="payment-body-info">
                      <div>
                        <Title as="h3" size="sm" noMarginBottom>
                          {isLinkPaymentMethod ? "Link" : paymentMethod.card?.brand ?? CardBrands.UNKNOWN}
                        </Title>
                        {!isLinkPaymentMethod && (
                          <Text as="p" size="sm" noMarginBottom>
                            {`${paymentMethod.card?.funding ?? ""} card ending in ${paymentMethod.card?.last4 ?? ""}`}
                          </Text>
                        )}
                      </div>
                    </div>
                    <div className="payment-body-price">
                      {isLinkPaymentMethod ? (
                        <img src={LinkLogo} width={28} data-testid="link-payment-method-icon" />
                      ) : (
                        <img src={CardLogoIndex.get(paymentMethod.card?.brand ?? CardBrands.UNKNOWN)} width={45} />
                      )}
                    </div>
                  </PaymentBody>
                </PaymentOption>
                {!user.isPending && view === PaymentMethodManagerView.PROFILE_SETTINGS && (
                  <Button
                    size="sm"
                    iconName="Trash"
                    isIconOnly
                    onClick={() => deleteMethod(paymentMethod.id)}
                    variant="outline"
                    data-testid={`delete-payment-method-${paymentMethod.id}`}
                  />
                )}
              </PaymentContainer>
            );
          })}
        </>
      )}
      {paymentMethods.length > 0 && !user.isPending ? (
        <>
          {managerSetup !== PaymentManagerSetup.SET_DEFAULT &&
          managerSetup !== PaymentManagerSetup.ADD ? (
            <AddEditButtonsContainer>
              <Button
                size="md"
                iconName="ArrowLeft"
                onClick={closePaymentForm}
                isIconOnly
                variant="outline"
              />
            </AddEditButtonsContainer>
          ) : managerSetup === PaymentManagerSetup.SET_DEFAULT ? (
            <AddPaymentMethodButtonWrapper onClick={addMethod}>
              <Button size="md" iconName="Plus" isIconOnly variant="outline" />
              <Text>Add a new payment method</Text>
            </AddPaymentMethodButtonWrapper>
          ) : null}
        </>
      ) : (
        user.isPending && (
          <div className="loader-img">
            {" "}
            <img src={Loader} />
          </div>
        )
      )}
      {managerSetup === PaymentManagerSetup.ADD && (
        <PaymentForm
          stripeHook={stripeHook}
          stripeElementsHook={stripeElementsHook}
          save={save}
          view={view}
          paymentMethods={paymentMethods}
          editablePaymentMethodId={user.data.defaultPaymentMethod}
          onClose={closePaymentForm}
        />
      )}
    </>
  );

  const isCheckoutView = view === PaymentMethodManagerView.CHECKOUT;

  return (
    <>
      <Card className={`manager-wrapper${cardHidden ? "-card-hidden" : ""}`}>
        {isCheckoutView ? (
          <>
            {/* ExpressCheckoutElement must always mount (even if it turns out
                unusable) — its own "ready" event is how we learn whether
                Link is actually available, so gating its render on
                canUseLink would mean it can never report readiness. The row
                itself is CSS-hidden rather than unmounted when nothing in it
                ends up available, avoiding an empty gap above the gateways. */}
            <QuickPayRow className={canUseApplePay || canUseLink ? undefined : "quick-pay-row-hidden"}>
              {isProcessingPayment && (
                <div className="quick-pay-processing-overlay" data-testid="quick-pay-processing-overlay">
                  <img className="quick-pay-processing-loader" src={Loader} width={40} />
                </div>
              )}
              {canUseApplePay && paymentRequest && (
                <div className="quick-pay-apple-pay">
                  <PaymentRequestButtonElement
                    options={{
                      paymentRequest,
                      style: {
                        paymentRequestButton: { type: "default", theme: "dark", height: "48px" },
                      },
                    }}
                    onClick={(event) => {
                      if (onApplePayButtonClick?.() === false) {
                        event.preventDefault();
                      }
                    }}
                  />
                </div>
              )}
              {/* Restricted to Link only — Apple Pay already has its own
                  button above, and we don't want ExpressCheckoutElement
                  rendering a second, duplicate Apple/Google Pay button. */}
              <div
                className={`quick-pay-link${canUseLink ? "" : " quick-pay-link-hidden"}`}
                data-testid="link-express-checkout"
              >
                <ExpressCheckoutElement
                  options={{
                    buttonHeight: 48,
                    wallets: { applePay: "never", googlePay: "never" },
                  }}
                  onReady={onLinkReady}
                  onConfirm={onLinkConfirm ?? (() => {})}
                />
              </div>
            </QuickPayRow>
            {/* Only makes sense once there's something above to separate from --
                mirrors the QuickPayRow's own canUseApplePay || canUseLink gate. */}
            {(canUseApplePay || canUseLink) && (
              <QuickPaySeparator>Or pay with a card</QuickPaySeparator>
            )}
            {/* Credit Card gateway */}
            <GatewayCard
              data-testid="gateway-credit-card"
              aria-selected={selectedGateway === PaymentGateway.CREDIT_CARD}
              $selected={selectedGateway === PaymentGateway.CREDIT_CARD}
              onClick={() => handleGatewaySelect(PaymentGateway.CREDIT_CARD)}
            >
              <Title as="h3" size="sm" noMarginBottom>
                Credit Card
              </Title>
              <div className="col-md-6 cards-svg-alt">
                <img src={VisaSvg} alt="" />
                <img src={MasterCardSvg} alt="" />
                <img src={AmexSvg} alt="" />
                <img src={Discover} alt="" />
              </div>
            </GatewayCard>
            {selectedGateway === PaymentGateway.CREDIT_CARD && renderCreditCardContent()}
            <hr></hr>
            {/* HSA/FSA gateway */}
            <GatewayCard
              data-testid="gateway-hsa-fsa"
              aria-selected={selectedGateway === PaymentGateway.HSA_FSA}
              $selected={selectedGateway === PaymentGateway.HSA_FSA}
              onClick={() => handleGatewaySelect(PaymentGateway.HSA_FSA)}
            >
              <Title as="h3" size="sm" noMarginBottom>
                Pay with HSA/FSA
              </Title>
              <img src={WithFlexLogo} alt="WithFlex Logo" width={40} />
            </GatewayCard>
            {selectedGateway === PaymentGateway.HSA_FSA && (
              <Text
                as="p"
                size="md"
                className="redirect-message"
                data-testid="hsa-fsa-redirect-message"
              >
                You'll be redirected to Flex - Pay with HSA/FSA to complete your purchase.
              </Text>
            )}
          </>
        ) : (
          renderCreditCardContent()
        )}
      </Card>
      <ConfirmationModal
        open={displayConfirmationModal}
        title={"Are you sure you want to delete this card?"}
        message={renderDeleteConfirmationMessage()}
        handleChoice={(confirmation: boolean) => {
          confirmDeleteMethod(confirmation);
        }}
      />
    </>
  );
};

export default PaymentMethodManager;
